import {
  QueueManagerStatus,
  event,
  AdUnitStatus,
  AdFormat,
  DestroyReason,
  ContentType,
  PlayerType,
  TagsGroup,
  ACTIVE_UNIT_STATUSES,
  IFrameScopeName,
  Platform, YANDEX_SDK_URL, ADMAN_URL
} from '../utils/const';

import YandexRTB from '../modules/Yandex/YandexRTB';
import AdMan from '../modules/AdMan/AdMan';

import {
  getRandomInteger,
  getRandomString, logOnServerDebugInfo,
  replaceBaseURLMacroses
} from "../utils/utils";
import { API } from "../utils/api";
import createVPAIDModule, { VPAIDModule } from "../modules/VPAID/VPAIDModule";
import YandexInstream from "../modules/Yandex/YandexInstream";
import { PageObserver } from "../utils/PageObserver";

import LibLoader from "../utils/LibLoader";
import IFrameScope from "./IFrameScope";
import VPAID from "../modules/VPAID/VPAID";
import Gnezdo from "../modules/Gnezdo/Gnezdo";
import GoogleIMA from "../modules/Google/GoogleIMA";
import GooglePubTag from "../modules/Google/GooglePubTag";

// const LOW_PRICE_TIDS = [29467, 28468, 28469, 28475, 28476];

class QueueManager {
  constructor({ config, emitter, logger, pageObserver, serverLogger, vastParser }) {
    this.queue = [
      [], // TagsGroup.ZERO
      [], // TagsGroup.YANDEX
      [], // TagsGroup.ADMAN
      [], // TagsGroup.IN_PROCESS
      [], // TagsGroup.CONTENT
    ];
    this.emitter = emitter;
    this.config = config;
    this.logger = logger;
    this.pageObserver = new PageObserver(null); // pageObserver;
    this.serverLogger = serverLogger;
    this.vastParser = vastParser; // TODO: put it in options

    Object.values(IFrameScopeName).forEach(scope => {
      this[scope] = null;
    })
    
    this.tags = [];
    this.unprocessedTags = [];

    this._status = QueueManagerStatus.IDLE;
    // this._T2 = null;
    this._T2Timestamp = 0;
    this.T2Paused = false;
    this._waitForInputClose = false;
    
    this.timers = {
      initAd: null,
    }

    this.currentTagsGroup = 0;
    this._groupsOrder = null;
    this._prevActiveUnit = null;
    this._currentGroupOrderIndex = 0;

    this.impressions = 0;
    this.requests = 1;
    this.startAttempts = 0;
    this.midrolls = 0;
    this.playedInCurrentTagsCycle = false; // TODO: use impressionsInCurrentTagsCycle
    this.ignorePrerollCondition = false;
    this.onNoAdsDelayed = false;
    this.playFullContentBlock = false;
    this.impressionsInCurrentTagsCycle = 0;
    this.initTimestamp = Date.now();
    this.tagsResponseTimestamp = 0;

    this.RPM = {};

    this._subscribeForEvents();
  }

  get length() {
    this.logger.log('[QM] length', this.queue.length);
    return this.queue.length;
  }

  get status() {
    return this._status;
  }

  set waitForInputClose(status) {
    if (this._waitForInputClose && !status) {
      this.logger.log('[QM] keyboard hidden. request tags...');
      this.emitter.emit(event.Player.TagsRequest);
    }

    this._waitForInputClose = status;
  }

  set status(status) {
    console.warn('[QM] changed QueueManager status to: ', status);
    this._status = status;
    this.emitter.emit(event.Player.QueueManagerStatusChanged, { status });
  }

  set prevActiveUnit(unit) {
    this._prevActiveUnit = unit;
  }

  get prevActiveUnit() {
    return this._prevActiveUnit;
  }

  get activeUnit() {
    this.logger.log('[QM] get activeUnit');
    if (!this._groupsOrder) this._groupsOrder = this._getGroupsOrder();
    // this.currentTagsGroup = this.currentTagsGroup === -1 ? this._getMostProfitableTagsGroup() : this._getNextTagsGroup(this.currentTagsGroup);
    // this.currentTagsGroup = this._getNextTagsGroup();
    // console.log('currentTagsGroup = ', this.currentTagsGroup);

    const activeUnit = this.queue[this.currentTagsGroup].find(unit => ACTIVE_UNIT_STATUSES.includes(unit.status));
    if (activeUnit) return activeUnit;

    this.currentTagsGroup = this._getNextTagsGroup();
    this.logger.log('currentTagsGroup = ', this.currentTagsGroup);

    let loadedUnits = this.queue[this.currentTagsGroup].filter(unit => unit.status === AdUnitStatus.LOADED);
    if (!this.pageObserver.isTabActive) {
      const deprecated = [Platform.YANDEX, Platform.YANDEX_ADS_SDK, Platform.ADMAN];
      loadedUnits = loadedUnits.filter(unit => !deprecated.includes(unit.config.params.platform));
    }
    this.logger.log('loaded units: ', loadedUnits);

    if (loadedUnits.length) {
      const activeUnit = this._getActiveUnit(0, loadedUnits);

      if (activeUnit) {
        activeUnit.status = AdUnitStatus.ACTIVE;
        this.logger.log('new active unit returned: ', activeUnit);

        return activeUnit;
      }
    } else if (this.currentTagsGroup === 0) {
      // this.tagsRequest(); DEBUG!!!
      // return;
    } else {
      return this.activeUnit;
    }

    return this.logger.log('[QM] queue is not empty but there are no ads to show');
  }

  addTag = (tag) => {
    this.tags.push(tag);
  }

  completeTag = (tid, success) => {
    const tag = this.tags.find((tag) => tag.tid === tid);

    if (!tag) return;
    tag.complete = true;
    tag.success = success;
    
    this.logger.log(`complete tag ${tag.tid}`, tag);
    if (this.unprocessedTags.length) this.startTagsRace(this.unprocessedTags.splice(0, 1), false);

    setTimeout(() => {
      if (this.checkEveryTagsComplete() && !this.T2Interval && this.status === QueueManagerStatus.WAIT_FOR_READY_TAGS) {
        this.emitter.emit(event.Player.TagsRequest);
      }
    }, 0);
  }

  checkEveryTagsComplete = () => this.tags.every((tag) => tag.complete);

  addUnit(unit) {
    this.logger.log('[QM] addUnit', unit);
    this.queue[unit.config.params.groupId].push(unit);

    if (unit.config.params.groupId !== TagsGroup.CONTENT) {
      this.queue[unit.config.params.groupId] = this._sortAds([...this.queue[unit.config.params.groupId]]);
    }
    // this._groupsOrder = this._getGroupsOrder();
  }

  getUnit(advID, groupId = this.currentTagsGroup) {
    this.logger.log('[QM] getUnit', advID);
    return this.queue[groupId].find(unit => unit.advID === advID);
  }

  removeUnit(advID, groupId = this.currentTagsGroup) {
    this.logger.log('[QM] removeUnit', advID);
    this.queue[groupId] = this.queue[groupId].filter(item => item.advID !== advID);
  }

  loadedUnitsExistInGroup = (groupId) => this.queue[groupId].some(unit => unit.status === AdUnitStatus.LOADED);

  cleanup() {
    this.logger.log('[QM] cleanup slots & queue');
    // this.queue.forEach(group => group.forEach(unit => { unit.remove(); })); //  clearTimeout(unit.timers.reuse);
    // this.queue[0].forEach(unit => { if (unit !== this.prevActiveUnit) unit.remove(); });
    this.queue = [[...this.queue[TagsGroup.ZERO].filter(unit => {
      if (unit === this.prevActiveUnit) return true;

      unit.remove();
      return false;
    })], [...this.queue[TagsGroup.IN_PROCESS].filter(unit => {
      if (unit.config.params.groupId !== TagsGroup.CONTENT) unit.remove();

      return false;
    })], [...this.queue[TagsGroup.YANDEX]], [...this.queue[TagsGroup.ADMAN]], [...this.queue[TagsGroup.CONTENT]]];
    this.tags = []; // cleanup tags

    if (this.config.secure.ya && this.requests > 1 && this.requests % (this.config.yaRefresh || 1) === 0) {
      this.emitter.emit(event.Player.RefreshIFrameScope, { scopeName: IFrameScopeName.YANDEX });
      // if (this.config.content.type === ContentType.TAGS) this.emitter.emit(event.Player.RefreshIFrameScope, { scopeName: IFrameScopeName.YANDEX_CONTENT });

      if (this.prevActiveUnit) {
        this.emitter.emit(event.Player.PlayerVisibilityChangeRequest, false);
        this.prevActiveUnit = null;
      }
    }

    if (this.config.secure.adman && this.requests > 1 && this.requests % (this.config.admanRefresh || 1) === 0) {
      this.emitter.emit(event.Player.RefreshIFrameScope, { scopeName: IFrameScopeName.ADMAN });
      // if (this.config.content.type === ContentType.TAGS) this.emitter.emit(event.Player.RefreshIFrameScope, { scopeName: IFrameScopeName.ADMAN_CONTENT });
    }
  }

  _getActiveUnit(index, unitList) {
    const isLastAd = index + 1 >= unitList.length;
    const ad = unitList[index];

    if (ad.config.params.settings && ad.config.params.settings.RPM) {
      const { RPM } = ad.config.params.settings;
      const groupName = Object.keys(RPM)[0];

      if (this.RPM[groupName]) {
        const diff = Date.now() - this.RPM[groupName];
  
        this.logger.log('diff = ', diff);
        this.logger.log(`${groupName} = `, RPM[groupName] * 1000);
        if (diff > RPM[groupName] * 1000) {
          this.RPM[groupName] = Date.now();

          return ad;
        }
  
        if (!isLastAd) {
          return this._getActiveUnit(index + 1, unitList);
        } else {
          // if (this.playedInCurrentTagsCycle || this.requests <= 2) {
          //   this.cleanup();
            
          //   if (this.config.type === PlayerType.STREAM) {
          //     this.status = QueueManagerStatus.CONTENT_PLAYING;
          //   } else {
          //     this.emitter.emit(event.Player.TagsRequest);
          //   }
          // } else {
          //   this.emitter.emit(event.Player.OnNoAds);
          // }

          // this.tagsRequest(); DEBUG!!!
          return null;
        } 
      } else {
        this.RPM[groupName] = Date.now();

        return ad;
      }
    }

    return ad;
  }
  
  sortTags = (tags) => {
    const sortedTags = tags.sort((a, b) => {
      a.priority = +a.priority || 1;
      b.priority = +b.priority || 1;
      
      const aPrice = +a.price || 0;
      const bPrice = +b.price || 0;
      
      // a.config.params.price = +a.config.vastPrice || +a.config.params.price || 0;
      // b.config.params.price = +b.config.vastPrice || +b.config.params.price || 0;
      
      if (a.priority === b.priority) {
        return bPrice - aPrice;
      }
      return a.priority - b.priority;
    });
    
    this.logger.log('[QM] sort tags', sortedTags);
    
    return sortedTags;
  }

  _sortAds (ads) {
    const sortedAds = ads.sort((a, b) => {
      a.config.params.priority = +a.config.params.priority || 1;
      b.config.params.priority = +b.config.params.priority || 1;

      const aPrice = +a.config.vastPrice || +a.config.params.price || 0;
      const bPrice = +b.config.vastPrice || +b.config.params.price || 0;

      if (a.config.params.priority === b.config.params.priority) {
        return bPrice - aPrice;
      }
      return a.config.params.priority - b.config.params.priority;
    });

    this.logger.log('[QM] sort ads', sortedAds);

    return sortedAds;
  }

  _someAdsLoaded = () => [this.queue[TagsGroup.ZERO], this.queue[TagsGroup.YANDEX], this.queue[TagsGroup.ADMAN]].some(group => group.some(unit => unit.status === AdUnitStatus.LOADED));
  someAdActive = () => [this.queue[TagsGroup.ZERO], this.queue[TagsGroup.YANDEX], this.queue[TagsGroup.ADMAN]].some(group => group.some(unit => unit.status === AdUnitStatus.PLAYING || unit.status === AdUnitStatus.ACTIVE));
  someAdPlaying = () => [this.queue[TagsGroup.ZERO], this.queue[TagsGroup.YANDEX], this.queue[TagsGroup.ADMAN]].some(group => group.some(unit => unit.status === AdUnitStatus.PLAYING));
  _allAdsFinished = () => /* this.unprocessedTags.length === 0 &&  DEBUG!!! */ this.checkEveryTagsComplete() && [this.queue[TagsGroup.ZERO], this.queue[TagsGroup.YANDEX], this.queue[TagsGroup.ADMAN]].some(group => group.length > 0) && [this.queue[TagsGroup.ZERO], this.queue[TagsGroup.YANDEX], this.queue[TagsGroup.ADMAN]].every(group => group.every(unit => unit.status === AdUnitStatus.FINISHED || unit.status === AdUnitStatus.ERROR));
  _someAdsInit = () => [this.queue[TagsGroup.ZERO], this.queue[TagsGroup.YANDEX], this.queue[TagsGroup.ADMAN]].some(group => group.some(unit => unit.status === AdUnitStatus.INIT));

  _getGroupsOrderByTemplate = (template = 0) => {
    if (template === 0) return this._defaultGroupsOrder; // [0, 1, 2]

    const order = [];
    if (template === 1) { // [2, 0, 1, 0]
      this._defaultGroupsOrder.forEach((groupId, idx) => {
        if (groupId !== 0 && this._defaultGroupsOrder[idx + 1] !== 0) {
          order.push(groupId, 0);
        } else {
          order.push(groupId);
        }
      });

      return order;
    }
  }

  _getGroupsOrder = () => {
    const units = [...this.queue[0], ...this.queue[1], ...this.queue[2]]; // .filter(unit => unit.status === AdUnitStatus.LOADED); // TODO: come up with something normal
    const sorted = this._sortAds([...units]);
    this._defaultGroupsOrder = [...new Set([...sorted.map(unit => unit.config.params.groupId)])];
    
    return this._getGroupsOrderByTemplate(0);
  }

  updateGroupsOrder = (template) => {
    this._groupsOrder = this._getGroupsOrderByTemplate(template);
  }

  // _getMostProfitableTagsGroup = () => { // TODO: rename this funciton
  //   const units = [...this.queue[0], ...this.queue[1], ...this.queue[2]].filter(unit => unit.status === AdUnitStatus.LOADED); // TODO: come up with something normal
  //   const sorted = this._sortAds([...units]);
    
  //   this._groupsOrder = [...new Set(sorted.map(unit => unit.config.params.groupId))];

  //   return sorted[0] ? sorted[0].config.params.groupId : -1;
  // }

  _getNextTagsGroup = (current = this._currentGroupOrderIndex) => { // TODO: check it in different cases.
    // const next = current === this.length - 1 ? 0 : current + 1;

    // if (this.queue[next].length === 0 && next === 0) return next;

    const nextGroup = this._groupsOrder[this._currentGroupOrderIndex];
    this._currentGroupOrderIndex = this._currentGroupOrderIndex === this._groupsOrder.length - 1 ? 0 : this._currentGroupOrderIndex + 1;
    
    return this.queue[nextGroup].length ? nextGroup : this._getNextTagsGroup();
  }
  
  _checkImpressionsLimit = () => this.impressions >= this.config.Impression;
  _checkMaxrunLimit = () => this.requests > this.config.MaxRun; // TODO: создать отдельный счетчик для коррекнтной работы tagsResponse.data.reuse

  // tagsRequest() {
  //   if (this.playedInCurrentTagsCycle || this.requests <= 2) {
  //     this.cleanup();
  //
  //     if (this.config.type === PlayerType.STREAM || this.config.type === PlayerType.EMBED) {
  //       this.status = QueueManagerStatus.CONTENT_PLAYING;
  //     } else {
  //       this.emitter.emit(event.Player.TagsRequest);
  //     }
  //   } else {
  //     this.emitter.emit(event.Player.OnNoAds);
  //   }
  // }

  _subscribeForEvents() {
    this.emitter.on(event.Player.AdUnitStatusChanged, ({ unit, status }) => {
      this.logger.log(`[QM] unit with advID ${unit.advID} changed status to ${status}`);
      this.emitter.emit(event.Player.RefreshPlayerCheckAliveTimer, { target: 'AdUnit status change' });

      switch (status) {
        case AdUnitStatus.LOADED:
          if (this.status === QueueManagerStatus.WAIT_FOR_READY_TAGS) {
            this.status = QueueManagerStatus.READY_TO_PLAY;
          }

          // else if (this.queue.every(unit => unit.status === AdUnitStatus.LOADED) && this.status === QueueManagerStatus.IDLE) {
          //   this.logger.log('[QM] every tag complete');
          // }
          break;
        case AdUnitStatus.ACTIVE:
          break;
        case AdUnitStatus.PLAYING:
          // const unit = this.getUnit(advID);

          if (!unit) return;

          // this.status = QueueManagerStatus.PLAYING;
          if (!unit.impressionEmitted && this.status !== QueueManagerStatus.PLAYING && unit.config.params.groupId !== TagsGroup.CONTENT) {
            this.impressions += 1;
            this.impressionsInCurrentTagsCycle += 1;
            this.playedInCurrentTagsCycle = true;
            // unit.impressionEmitted = true;

            const ignorePrerolls = this.requests > 2 && this.impressions > 0;
            if (this.impressions > this.config.content.preroll || ignorePrerolls) {
              this.midrolls = this.midrolls >= this.config.content.midroll ? 1 : this.midrolls + 1;
            }

            if (unit.config.params.format === AdFormat.GOOGLE) {
              this.queue = this.queue.filter(u => u.advID === unit.advID || u.config.params.format !== AdFormat.GOOGLE);
            }
          }
          // this.playedInCurrentTagsCycle = true;
          if (unit.config.params.groupId !== TagsGroup.CONTENT) this.status = QueueManagerStatus.PLAYING;

          if (this.T2Interval) {
            clearInterval(this.T2Interval);
            this.T2Interval = null;

            this._T2Timestamp = 0;
            this.T2Paused = false;
          }
          // if (this._T2) {
          //   clearTimeout(this._T2);
          //   this._T2 = null;
          // }
          break;
        case AdUnitStatus.ERROR:
          if (this.T2Interval) {
            this.T2Paused = false;
          }
        case AdUnitStatus.FINISHED:
          if (
            unit.config.params.groupId !== TagsGroup.CONTENT &&
            this.status !== QueueManagerStatus.PLAYING &&
            this.status !== QueueManagerStatus.READY_TO_PLAY &&
            this.status !== QueueManagerStatus.WAIT_FOR_READY_TAGS &&
            this.status !== QueueManagerStatus.WAIT_FOR_VIEW
          ) return; // TODO: check it on different player types

          if (unit.config.params.groupId === TagsGroup.CONTENT && !this.someAdActive()) {
            this.emitter.emit(event.TagsContent.Play);
            return;
          }

          if (this.status === QueueManagerStatus.WAIT_FOR_VIEW) {
            const someAdsLoaded = this._someAdsLoaded(); // this.queue.some(unit => unit.status === AdUnitStatus.LOADED);
            const someAdsInit = this._someAdsInit();
            if (!someAdsLoaded && !someAdsInit) {
              this.status = QueueManagerStatus.IDLE;

              // this.cleanup();
              if (this.config.type === PlayerType.STREAM || this.config.type === PlayerType.EMBED) {
                if (this.impressionsInCurrentTagsCycle === 0) {
                  this.playFullContentBlock = false;
                  this.emitter.emit(event.Player.TagsRequest);
                }

                this.status = QueueManagerStatus.CONTENT_PLAYING;
              } else {
                this.emitter.emit(event.Player.TagsRequest);
              }
            }

            return;
          }

          const queueHasActiveUnit = [this.queue[TagsGroup.ZERO], this.queue[TagsGroup.YANDEX], this.queue[TagsGroup.ADMAN]].some(group => group.some(unit => unit.status === AdUnitStatus.PLAYING)); // TODO: сделать функцию, возращающию первые 3 группы

          if (!queueHasActiveUnit) {
            this.logger.log('targetUnit: ', unit);

            if (this.config.Impression === this.impressions) {
              this.status = QueueManagerStatus.MAX_IMPRESSION;
            } else if (unit.status !== AdUnitStatus.ERROR && unit.shouldContentBeShown({
              impressionsAmount: this.impressions,
              impressionsInCTC: this.impressionsInCurrentTagsCycle,
              midrollCounter: this.midrolls
            })) {
              this.playFullContentBlock = true;
              this.status = QueueManagerStatus.CONTENT_PLAYING;
            } else {
              this.status = QueueManagerStatus.READY_TO_PLAY;
            }
          }
          break;
        default:
          break;
      }
    });

    this.emitter.on(event.Player.QueueManagerStatusChanged, () => {
      this.emitter.emit(event.Player.RefreshPlayerCheckAliveTimer, { target: 'QueueManager status change' });

      // setTimeout(() => {
      switch (this._status) {
        case QueueManagerStatus.READY_TO_PLAY:
          this.logger.log('[QM] status changed to readyToPlay');

          const someAdsLoaded = this._someAdsLoaded(); // this.queue.some(unit => unit.status === AdUnitStatus.LOADED);
          const allFinished = this._allAdsFinished(); // this.checkEveryTagsComplete && this.queue.every(unit => unit.status === AdUnitStatus.FINISHED || unit.status === AdUnitStatus.ERROR); // this.length > 0
          const adPlaying = this.someAdActive(); // this.queue.some(unit => unit.status === AdUnitStatus.PLAYING || unit.status === AdUnitStatus.ACTIVE);

          if (adPlaying) return console.warn('[QM] ad start was prevented because some ad is already playing'); // this.logger.log('[QM] ad start was prevented because some ad is already playing');

          this.logger.log('impressions: ', this.impressions);
          if (this._checkImpressionsLimit()) {
            this.status = QueueManagerStatus.MAX_IMPRESSION;
            return;
          }

          if (someAdsLoaded) {
            this.logger.log('[QM] start...');
            if (this.T2Interval) {
              this.T2Paused = true;
            }

            this.emitter.emit(event.Player.AdStartRequest);
          } else if (allFinished) {
            this.status = QueueManagerStatus.IDLE; // TODO: create function for cleanup & tag request

            // this.cleanup();
            if (this.config.type === PlayerType.STREAM || this.config.type === PlayerType.EMBED) { // TODO: ???
              // if (this.requests > 2 && !this.playedInCurrentTagsCycle) this.emitter.emit(event.Player.TagsRequest);

              this.playFullContentBlock = true;
              if (this.impressionsInCurrentTagsCycle === 0) {
                this.playFullContentBlock = false;
                this.emitter.emit(event.Player.TagsRequest);
              }

              this.status = QueueManagerStatus.CONTENT_PLAYING;
            } else {
              this.emitter.emit(event.Player.TagsRequest); // TODO: clean this block
            }
          } else {
            if (!this.T2Interval) {
              this._T2Timestamp = Date.now();
              this.T2Interval = setInterval(() => {
                if (this.T2Paused) return;

                if (Date.now() - this._T2Timestamp >= this.config.timeouts.waterfallend) {
                  clearInterval(this.T2Interval);
                  this.T2Interval = null;
                  this._T2Timestamp = 0;
                  this.T2Paused = false;

                  this.status = QueueManagerStatus.IDLE;
                  // this._T2 = null;

                  // this.cleanup();
                  this.emitter.emit(event.Player.TagsRequest);
                }
              }, 500);
            }

            if (this.checkEveryTagsComplete()) {
              this.emitter.emit(event.Player.TagsRequest);
            } else {
              this.status = QueueManagerStatus.WAIT_FOR_READY_TAGS;
            }
          }
          break;
        case QueueManagerStatus.MAX_IMPRESSION:
          this.logger.log('[QM] impression limit reached');
          this.emitter.emit(event.Player.PlayerDestroyRequest, { reason: DestroyReason.OTHER });

          break;
        case QueueManagerStatus.MAX_RUN:
          // this.logger.log('[QM] maxrun limit reached');
          this.emitter.emit(event.Player.PlayerDestroyRequest, this.impressions > 0 ? { reason: DestroyReason.OTHER } : { reason: DestroyReason.NOADS });
          break;
        case QueueManagerStatus.WAIT_FOR_VIEW:
          this.logger.log('[QM] status changed to waitForView');
          this.emitter.emit(event.Player.PlayerLoaded);

          break;
        case QueueManagerStatus.CONTENT_PLAYING:
          this.logger.log('[QM] status changed to contentPlaying');
          // if (this.config.type === PlayerType.EMBED && this.impressionsInCurrentTagsCycle === 0) this.playFullContentBlock = false;
          this.emitter.emit(event.Player.ContentStartRequest, { fullBlock: this.playFullContentBlock });
          setTimeout(() => {
            this.playFullContentBlock = false;
          }, 0);

          // if (this.config.type === PlayerType.EMBED) {
          //   // this.cleanup();
          //   this.emitter.emit(event.Player.TagsRequest);
          // }
          break;
        case QueueManagerStatus.PLAYING:
          break;
        case QueueManagerStatus.WAIT_FOR_READY_TAGS:
          this.logger.log('queue:', this.queue);
          if (this.config.content.mode === ContentType.STREAM) {
            this.emitter.emit(event.Player.ContentStartRequest);
          }
          break;
        default:
          this.logger.log('[QM] unhandled status change');
          break;
      }
      // }, 0);
    });

    this.emitter.on(event.Player.RefreshIFrameScope, ({ scopeName }) => {
      this.logger.log(`[QM] refresh ${scopeName}`);

      const Constructor = (scopeName === IFrameScopeName.YANDEX || scopeName === IFrameScopeName.YANDEX_CONTENT) ? YandexRTB : AdMan;

      this[scopeName].getIFrame().remove();
      this[scopeName] = new IFrameScope({
        container: this.uiManager.ui.adsContainer,
        source: (scopeName === IFrameScopeName.YANDEX || scopeName === IFrameScopeName.YANDEX_CONTENT) ? YANDEX_SDK_URL : ADMAN_URL,
        emitter: this.emitter,
        prefix: scopeName
      });

      const group = (scopeName === IFrameScopeName.YANDEX_CONTENT || scopeName === IFrameScopeName.ADMAN_CONTENT) ? this.queue[TagsGroup.CONTENT] : this.queue[TagsGroup.ZERO];

      group.forEach(unit => {
        if (unit instanceof Constructor) {
          if (unit.status === AdUnitStatus.ERROR || unit.status === AdUnitStatus.FINISHED) return;

          this.emitter.emit(
            event.Player.AdPreloaded,
            new Constructor({ ...unit.config, scope: this[scopeName] }),
          );

          unit.status = AdUnitStatus.ERROR;
        }
      });
    });
  }

  reuseUnit = (unit) => {
    this.logger.log('[QM] reuse unit', unit);

    const constructorByPlatform = {
      [Platform.YANDEX]: YandexRTB,
      [Platform.ADMAN]: AdMan,
      [Platform.VPAID]: VPAID,
      [Platform.GNEZDO]: Gnezdo,
    }
    const { groupId } = unit.config.params;

    this.removeUnit(unit.advID);

    if (groupId === TagsGroup.CONTENT) {
      this.emitter.emit(event.Player.AdPreloaded, new constructorByPlatform[unit.config.params.platform](unit.config));
    } else {
      this.emitter.emit(event.Player.AdPreloaded, (groupId === TagsGroup.YANDEX ? new YandexRTB(unit.config) : new AdMan(unit.config))); // TODO: need refactoring
    }

    // if (groupId === TagsGroup.YANDEX) {
      // this.removeUnit(unit.advID);
      // this.emitter.emit(event.Player.AdPreloaded, new YandexRTB(unit.config));

      // const groupUnits = this.queue[groupId]; // .filter(u => unit !== u); // selected group units except current unit
      // const finishedUnits = groupUnits.filter(unit => unit.status === AdUnitStatus.ERROR || unit.status === AdUnitStatus.FINISHED);

      // console.log('gu2', groupUnits, finishedUnits);

      // finishedUnits.forEach(unit => {
      //   const { config } = unit;

      //   console.log('remove & reuse unit: ', unit, unit.status);

      //   this.removeUnit(unit.advID, unit.config.params.groupId);
      //   this.emitter.emit(event.Player.AdPreloaded, new YandexRTB(config));
      // });
    // }
  }

  reuseUnitsGroup = (groupId) => {
    const groupUnits = this.queue[groupId]; // .filter(u => unit !== u); // selected group units except current unit
    // const finishedUnits = groupUnits.filter(unit => unit.status === AdUnitStatus.ERROR || unit.status === AdUnitStatus.FINISHED);

    const constructorByPlatform = { // TODO: need refactoring
      [Platform.YANDEX]: YandexRTB,
      [Platform.ADMAN]: AdMan,
      [Platform.VPAID]: VPAID,
      [Platform.GNEZDO]: Gnezdo,
    }

    groupUnits.forEach(unit => { // finishedUnits
      const { config } = unit;

      this.removeUnit(unit.advID, config.params.groupId);

      if (groupId === TagsGroup.CONTENT) {
        if (unit.config.params.platform === Platform.VPAID) {
          new constructorByPlatform[unit.config.params.platform](unit.config)
        } else {
          this.emitter.emit(event.Player.AdPreloaded, new constructorByPlatform[unit.config.params.platform](unit.config));
        }
      } else {
        this.emitter.emit(event.Player.AdPreloaded, groupId === TagsGroup.YANDEX ? new YandexRTB(config) : new AdMan(config));
      }
    });
  }

  tagsRequest = ({ width, height }) => new Promise(resolve => {
    if (this.uiManager.isPlayerHiddenDueInputHide()) {
      this.logger.log('[QM] wait for keyboard hide before tags request');
      this._waitForInputClose = true;
      return;
    }

    // this.logger.log('[QM] request tags: ', this.requests);

    if (this.requests > 1) {
      if (this.queue[this.currentTagsGroup].length) this.serverLogger.logTags(this.queue[this.currentTagsGroup].map(unit => unit.config.params.asid));

      // if (this.config.content.type === ContentType.TAGS && this.queue[TagsGroup.CONTENT].length) {
      //   this.emitter.emit(event.TagsContent.Play);
      // }

      if (this._checkMaxrunLimit()) {
        this.status = QueueManagerStatus.MAX_RUN;
        return;
      }

      if (this.T2Interval && this._someAdsInit()) {
        this.logger.log('[QM] wait T2 before OnNoAds');

        this.onNoAdsDelayed = true;
        this.T2Paused = false;

        this.status = QueueManagerStatus.WAIT_FOR_READY_TAGS;

        return;
      } else if (this.impressionsInCurrentTagsCycle === 0 && !this.onNoAdsDelayed && this.requests > 2) {
        this.emitter.emit(event.Player.OnNoAds);

        return;
      }

      this.emitter.emit(event.TagsContent.ClearTagsContentFallback);
      this.cleanup();

      this.impressionsInCurrentTagsCycle = 0;
    }

    this.logger.log('[QM] request tags: ', this.requests);

    this.onNoAdsDelayed = false;
    if (this.config.type === PlayerType.EMBED || this.config.type === PlayerType.STREAM) this.midrolls = 0;

    const { tdom, sid, zone, playerVersion } = this.config;
    // const { width, height } = getSlotSizes(this.slot);
    // const width = 432; const height = 243;

    const playerLocation = window.top.document.location.href;
    // const url = convertBufferToString(TAG_REQUEST_URL).replace('(tdom)', tdom).replace('(zone_id)', zone).replace('(sid)', sid); // 'https://' + tdom + '/abcdef/' + sid + '/';
    const url = TAG_REQUEST_URL.replace('(tdom)', tdom).replace('(zone_id)', zone).replace('(sid)', sid); // 'https://' + tdom + '/abcdef/' + sid + '/';
    let requestParams = {};

    if (IS_FLYADS) {
      requestParams = {
        w: width,
        h: height,
        v: playerVersion,
        // n: this.requestCount++,
        n: this.requests++,
        cb: Date.now(),
        page: playerLocation,
        sid: sid,
        g: window.flyGdpr || 0,
        ga: window.flyGdprAllowed || 0,
        u: window.vadsUUID || '',
      };
    } else {
      requestParams = {
        sid: sid,
        zone: zone,
        w: width,
        h: height,
        v: playerVersion,
        // rc: this.requestCount++,
        rc: this.requests++,
        cb: Date.now(),
        page_url: playerLocation,
      };
    }

    const searchParams = new URLSearchParams(window.location.search);
    const searchedKeys = ['playertester', 'testtid'];

    searchedKeys.forEach((key) => {
      if (searchParams.has(key)) {
        if (key === 'playertester') requestParams.test = 1;
        if (key === 'testtid') requestParams.ttid = searchParams.get(key);
      }
    });

    this.emitter.emit(event.Player.RefreshPlayerCheckAliveTimer, { target: 'tags request' });
    LibLoader.clearWaitlist();

    if (this.tagsResponse && this.reuseCount < this.tagsResponse.data.reuse) {
      this.logger.log(`reuse tagsResponse. counter: ${this.reuseCount}`, this.tagsResponse);
      this.reuseCount++;

      this.unprocessedTags = this.sortTags([...this.tagsResponse.data.tags]);
      resolve(this.unprocessedTags.splice(0, this.config.tagsChunkSize || 10));
    } else {
      API.call(url, requestParams).then(tags => tags.json())
      .then(tagsResponse => {
        this.tagsResponse = tagsResponse;
        this.reuseCount = 0;

        this.unprocessedTags = this.tagsResponse && [...this.tagsResponse.data.tags];
        const tagsTrackers = this.tagsResponse && this.tagsResponse.data.trackers;

        this.logger.log('[QM] tags: ', this.unprocessedTags);

        this.tagsResponseTimestamp = Date.now();

        tagsTrackers && this.unprocessedTags && this.unprocessedTags.length && this.serverLogger.logTagsTrackers(tagsTrackers);
        // this.serverLogger.logTags(tags);

        if (this.tagsResponse.data.content && this.tagsResponse.data.content.length) {
          this.logger.log('[QM] content tags: ', this.tagsResponse.data.content);
          this.unprocessedTags.push(...this.tagsResponse.data.content)
        }

        this.unprocessedTags = this.sortTags(this.unprocessedTags);

        resolve(this.unprocessedTags.splice(0, this.config.tagsChunkSize || 10));
      })
      .catch(error => {
        this.logger.log('error during tags request: ', error);

        this.emitter.emit(event.Player.OnNoAds);
      });
    }
  })

  startTagsRace = (tags, initAdTimeout = true) => new Promise((resolve, reject) => {
    if (!tags) {
      this.logger.log('startTagsRace didnt receive tags for some reason');
      return false;
    }
    
    // this.logger.log('[QM] start tags race');
    this.logger.log('[QM] process tags', tags);
    
    if (initAdTimeout) {
      if (this.timers.initAd) clearTimeout(this.timers.initAd);
      this.timers.initAd = setTimeout(() => { this.logger.log('waterfall timeout'); this.timers.initAd = null; resolve(); }, this.config.timeouts.waterfallstart);
    }
    
    this.queue.forEach((group, index) => {
      if (index > 0 && index !== TagsGroup.CONTENT) {
        group.forEach(unit => {
          if (!tags.some(tag => tag.tid === unit.config.params.tid)) {
            this.removeUnit(unit.advID, unit.config.params.groupId);
          }
        });
      }
    });
    
    const { sid, zone } = this.config;
    
    // const selectedTags = [];
    tags.forEach(async (tag, index) => {
      const freq = tag.settings && tag.settings.freq;
      
      if (freq && (getRandomInteger(0, 100) > freq) && tag.groupId !== TagsGroup.CONTENT) return this.logger.log(`skipped tag ${tag.asid} due freq parameter`);
      // selectedTags.push(tag);

      const params = {
        // tid: tag.asid,
        tid: tag.tid,
        aid: tag.aid,
        asid: tag.asid,
        format: tag.adformat,
        platform: tag.platform,
        multiads: tag.multiads || 0,
        price: tag.price,
        priority: tag.priority,
        playerSettings: tag.playerSettings,
        settings: tag.settings,
        cc: Math.random() >= 0.5,
        vpaid: tag.vpaid || 0,
        // ptid: `${this.queue.requests}-${index}`,
        groupId: this.config.type === PlayerType.STREAM ? 0 : tag.groupId,
      };
      
      const tagExistInQueue = tag.groupId !== 0 && this.queue[tag.groupId].some(unit => unit.config.params.tid === tag.tid);
      
      if (tagExistInQueue) return;
      
      const { uiManager, impressionDetector, contentManager } = this;
      
      let baseTrackers = {};
      let trackers = {};
      Object.entries(this.serverLogger.baseTrackingEvents).forEach(([eventName, eventTrackers]) => {
        eventTrackers.forEach((tracker) => {
          const replacedURL = replaceBaseURLMacroses(tracker, { sid, zone, tid: params.tid, aid: params.aid, uuid: window.vadsUUID });
          
          if (baseTrackers[eventName])
            baseTrackers[eventName].push(replacedURL);
          else
            baseTrackers[eventName] = [replacedURL];
        });
      });
      
      const id = `tag-${getRandomString()}`;
      this.addTag({ id, trackers, complete: false, ...params });
      
      if (tag.platform === Platform.VPAID) {
        createVPAIDModule({
          vastUrl: tag.url,
          emitter: this.emitter,
          playerNode: uiManager.ui.adsContainer,
          vastParser: this.vastParser,
          config: this.config,
          trackers,
          baseTrackers,
          params,
          tagID: id,
          timeouts: this.config.timeouts,
          socket: null, // this.socket,
          logger: this.logger,
          uiManager,
          queueManager: this,
          impressionDetector: impressionDetector,
        });
      } else if (tag.platform === Platform.GOOGLE) {
        const tagUrlTemplateDecoded = decodeURIComponent(tag.template);
        const tagUrl = JSON.parse(tagUrlTemplateDecoded).tagUrl.replace("(referer)", encodeURIComponent(window.location.href));

        new GoogleIMA({
          tagUrl,
          emitter: this.emitter,
          // config: this.config,
          playerNode: uiManager.ui.adsContainer,
          playerConfig: this.config,
          params,
          trackers,
          baseTrackers,
          contentVideoSlot: this.config.content.type === ContentType.VIDEO && contentManager.getVideoSlot ? contentManager.getVideoSlot() : null,
          tagID: id,
          timeouts: this.config.timeouts,
          socket: this.socket,
          logger: this.logger,
          uiManager,
          queueManager: this,
          impressionDetector: impressionDetector,
        });

        // this.emitter.emit(
        //   event.Player.AdPreloaded,
        //   new GoogleIMA({
        //     tagUrl,
        //     emitter: this.emitter,
        //     // config: this.config,
        //     playerNode: uiManager.ui.adsContainer,
        //     playerConfig: this.config,
        //     params,
        //     trackers,
        //     baseTrackers,
        //     // contentVideoSlot: (this.content.type === ContentType.VIDEO || this.content.type === ContentType.SEGMENTS_VIDEO) ? this.content.getContentVideoSlot() : null,
        //     contentVideoSlot: this.config.content.type === ContentType.VIDEO && contentManager.getVideoSlot ? contentManager.getVideoSlot() : null,
        //     tagID: id,
        //     timeouts: this.config.timeouts,
        //     socket: this.socket,
        //     logger: this.logger,
        //     uiManager,
        //     queueManager: this,
        //     impressionDetector: impressionDetector,
        //   }));
      } else if (tag.platform === Platform.YANDEX || tag.platform === Platform.ADFOX) {
        const template = decodeURIComponent(tag.template);

        this.emitter.emit(
          event.Player.AdPreloaded,
          new YandexRTB({ // createYandexModule
            template,
            playerConfig: this.config,
            emitter: this.emitter,
            playerNode: uiManager.ui.adsContainer,
            trackers,
            baseTrackers,
            params,
            platform: tag.platform,
            tagID: id,
            timeouts: this.config.timeouts,
            socket: null, // this.socket,
            logger: this.logger,
            uiManager,
            queueManager: this,
            impressionDetector: impressionDetector,
            scope: params.groupId === TagsGroup.CONTENT ? this.contentYandexRTBScope :  this.yandexRTBScope,
          })
        );
        // }
      } else if (tag.platform === Platform.YANDEX_ADS_SDK) {
        const template = decodeURIComponent(tag.template);
        
        this.emitter.emit(
          event.Player.AdPreloaded,
          new YandexInstream({
            template,
            playerConfig: this.config,
            emitter: this.emitter,
            playerNode: uiManager.ui.adsContainer,
            trackers,
            baseTrackers,
            params,
            platform: tag.platform,
            timeouts: this.config.timeouts,
            socket: null, // this.socket,
            logger: this.logger,
            uiManager,
            queueManager: this,
            impressionDetector: impressionDetector,
          }));
      } else if (tag.platform === Platform.ADMAN || tag.platform === Platform.VK) {
        const template = decodeURIComponent(tag.template);
        
        this.emitter.emit(
          event.Player.AdPreloaded,
          new AdMan({
            emitter: this.emitter,
            playerNode: uiManager.ui.adsContainer,
            playerConfig: this.config,
            params,
            trackers,
            baseTrackers,
            timeouts: this.config.timeouts,
            template,
            socket: null, // this.socket,
            logger: this.logger,
            uiManager,
            queueManager: this,
            content: { type: ContentType.NONE }, // null, //this.content,
            impressionDetector: impressionDetector,
            scope: params.groupId === TagsGroup.CONTENT ? this.contentAdmanRTBScope :  this.admanScope,
          })
        );
      } else if (tag.platform === Platform.GNEZDO) {
        const template = decodeURIComponent(tag.template);

        this.emitter.emit(
          event.Player.AdPreloaded,
          new Gnezdo({
            emitter: this.emitter,
            playerNode: uiManager.ui.adsContainer,
            playerConfig: this.config,
            params,
            trackers,
            baseTrackers,
            timeouts: this.config.timeouts,
            template,
            socket: null, // this.socket,
            logger: this.logger,
            uiManager,
            queueManager: this,
            content: { type: ContentType.NONE }, // null, //this.content,
            impressionDetector,
          })
        );
      } else if (tag.platform === Platform.GPT) {
        const template = decodeURIComponent(tag.template);
        
        this.emitter.emit(
          event.Player.AdPreloaded,
          new GooglePubTag({
            // tagUrl,
            emitter: this.emitter,
            config: this.config,
            playerNode: uiManager.ui.adsContainer,
            playerConfig: this.config,
            tagID: id,
            params,
            template,
            trackers,
            baseTrackers,
            timeouts: this.config.timeouts,
            // socket: this.socket,
            logger: this.logger,
            uiManager,
            queueManager: this,
            impressionDetector,
          })
        );
      }
    });
    
    // this.serverLogger.logTags(selectedTags);
    
    // return true;
  });
}

export default QueueManager;
